import { XIcon } from "lucide-react";
import {
  UNSTABLE_Toast as AriaToast,
  UNSTABLE_ToastContent as AriaToastContent,
  UNSTABLE_ToastRegion as AriaToastRegion,
  Button,
  Text,
  UNSTABLE_ToastQueue as ToastQueue,
} from "react-aria-components";

import { type AlertVariants, alertVariants } from "./alert";

export type ToastContent = {
  variant: AlertVariants["color"];
  title: string;
  description?: string;
  /** Optional dedup key. If a toast with this key is already visible, the new one is skipped. */
  key?: string;
  action?: {
    label: string;
    onPress: () => void;
  };
};

// This is a global toast queue, to be imported and called where ever you want to queue a toast via queue.add().
export const queue = new ToastQueue<ToastContent>({});

export function addToast(toast: ToastContent, timeout?: number) {
  if (
    toast.key &&
    queue.visibleToasts.some((t) => t.content.key === toast.key)
  ) {
    return;
  }

  queue.add(toast, {
    timeout: timeout ?? (toast.variant === "success" ? 5000 : undefined),
  });
}

export function ToastRegion() {
  return (
    // The ToastRegion should be rendered at the root of your app.
    <AriaToastRegion
      queue={queue}
      className="fixed right-4 bottom-4 z-[9999] flex max-w-sm flex-col-reverse gap-2 rounded-lg outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 focus-visible:outline-solid"
    >
      {({ toast }) => (
        <AriaToast
          toast={toast}
          className={alertVariants({
            color: toast.content.variant,
            className: "flex flex-col pl-3 pr-0 space-y-0",
          })}
        >
          <div className="flex flex-row items-center">
            <AriaToastContent className="flex min-w-0 flex-1 flex-col">
              <Text slot="title" className="text-sm font-semibold">
                {toast.content.title}
              </Text>
              {toast.content.description && (
                <Text slot="description" className="text-xs">
                  {toast.content.description}
                </Text>
              )}
            </AriaToastContent>
            <Button
              slot="close"
              aria-label="Close"
              className="pressed:bg-white/15 flex h-8 w-8 flex-none appearance-none items-center justify-center rounded-sm border-none bg-transparent p-0 outline-none [-webkit-tap-highlight-color:transparent] hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white focus-visible:outline-solid"
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </div>
          {toast.content.action && (
            <div className="mt-1 -mb-1 flex justify-start pr-2">
              <Button
                onPress={toast.content.action.onPress}
                className="pressed:bg-white/40 rounded bg-white/20 px-2 py-1 text-xs font-semibold hover:bg-white/30"
              >
                {toast.content.action.label}
              </Button>
            </div>
          )}
        </AriaToast>
      )}
    </AriaToastRegion>
  );
}
